import { useParams, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import ProductZoom from "../../Components/ProductZoom";
import Rating from "@mui/material/Rating";
import QuantityBox from "../../Components/QuantityBox";
import Button from "@mui/material/Button";
import { BsCartFill } from "react-icons/bs";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { MdOutlineCompareArrows } from "react-icons/md";
import { IoMdShare } from "react-icons/io";
import Tooltip from "@mui/material/Tooltip";
import RelatedProducts from "./RelatedProducts";
import { fetchDataFromApi, postData } from "../../utils/api";
import CircularProgress from "@mui/material/CircularProgress";
import { MyContext } from "../../App";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { addItemToCart, fetchCartItems } from "../../store/slices/CartSlice";
import {
  toggleWishlistItem,
  fetchWishlistItems,
} from "../../store/slices/WishlistSlice";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(MyContext);
  const dispatch = useDispatch();
  const { cartItems, cartLoading } = useSelector((state) => state.cart);
  const { wishlistItems } = useSelector((state) => state.wishlist);

  const [productData, setProductData] = useState(null);
  const [relatedProductData, setRelatedProductData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const [activeTabs, setActiveTabs] = useState(0);
  const [activeSize, setActiveSize] = useState(null);
  const [tabError, setTabError] = useState(false);
  const [productQuantity, setProductQuantity] = useState(1);
  const [quantityDetails, setQuantityDetails] = useState(0);
  const [rating, setRating] = useState(1);
  const [optimisticWishlistState, setOptimisticWishlistState] = useState(null);
  const [reviews, setReviews] = useState({
    productId: id,
    customerName: "",
    customerId: "",
    reviewText: "",
    customerRating: 1,
  });

  const [optimisticCartState, setOptimisticCartState] = useState(null);

  const cartProductIds = useMemo(
    () => cartItems.map((item) => item.productId?._id),
    [cartItems]
  );

  const isInCart =
    optimisticCartState !== null
      ? optimisticCartState
      : cartProductIds.includes(productData?._id);

  // Check if product is in wishlist using optimistic state or global state
  const isInWishlist =
    optimisticWishlistState !== null
      ? optimisticWishlistState
      : productData
      ? wishlistItems?.some((item) => item.productId?._id === productData._id)
      : false;

  const isActive = useCallback((index) => {
    setActiveSize(index);
    setTabError(false);
  }, []);

  // Memoized functions to prevent unnecessary re-renders
  const fetchProductData = useCallback(async () => {
    try {
      const res = await fetchDataFromApi(`/api/products/${id}`);
      setProductData(res);

      if (
        res?.productRam?.length === 0 &&
        res?.productWeight?.length === 0 &&
        res?.size?.length === 0
      ) {
        setActiveSize(1);
      }

      // Fetch related products
      const relatedRes = await fetchDataFromApi(
        `/api/products/subCatId?subCatId=${res?.subCatId}`
      );
      const filteredData = relatedRes?.products?.filter(
        (item) => item.id !== id
      );
      setRelatedProductData(filteredData || []);
    } catch (err) {
      console.error("Product fetch error:", err);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to load product details.",
      });
    }
  }, [id, context]);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetchDataFromApi(`/api/productReviews?productId=${id}`);
      setReviewsData(res.data || []);
    } catch (err) {
      console.error("Reviews fetch error:", err);
    }
  }, [id]);

  useEffect(() => {
    setReviews((prev) => ({ ...prev, productId: id }));
    window.scrollTo(0, 0);
    setActiveSize(null);

    // Fetch product data and reviews only once
    Promise.all([fetchProductData(), fetchReviews()]);

    // Fetch user data using async thunks only if not already loaded
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?._id) {
      // Only fetch if not already loaded
      if (cartItems.length === 0) {
        dispatch(fetchCartItems(user._id));
      }
      if (wishlistItems.length === 0) {
        dispatch(fetchWishlistItems(user._id));
      }
    }

    context.setEnableFilterTab(false);
  }, [id, fetchProductData, fetchReviews, dispatch, context]);

  // Reset optimistic state when wishlistItems change
  useEffect(() => {
    setOptimisticWishlistState(null);
  }, [wishlistItems]);

  // Reset optimistic cart state when cartItems change
  useEffect(() => {
    setOptimisticCartState(null);
  }, [cartItems]);

  const onChangeInput = useCallback((e) => {
    setReviews((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const changeRating = useCallback((e) => {
    const value = Number(e.target.value);
    setRating(value);
    setReviews((prev) => ({
      ...prev,
      customerRating: value,
    }));
  }, []);

  const addReview = useCallback(
    async (e) => {
      e.preventDefault();
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Please login to submit a review.",
        });
        return;
      }

      if (!reviews.reviewText) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Please add a review.",
        });
        return;
      }

      try {
        await postData("/api/productReviews/add", {
          ...reviews,
          customerName: user.name,
          customerId: user._id,
          productId: id,
        });

        const newReview = {
          customerName: user.name,
          customerId: user._id,
          review: reviews.reviewText,
          customerRating: reviews.customerRating,
          dateCreated: new Date().toISOString(),
        };
        setReviewsData((prev) => [newReview, ...prev]);

        setReviews({
          productId: id,
          customerName: "",
          customerId: "",
          reviewText: "",
          customerRating: 1,
        });
        setRating(1);

        context.setAlertBox({
          open: true,
          error: false,
          msg: "Review submitted successfully!",
        });
      } catch (err) {
        console.error("Review submission error:", err);
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Failed to submit review.",
        });
      }
    },
    [reviews, id, context]
  );

  const quantity = useCallback((val) => {
    setProductQuantity(val);
  }, []);

  const handleAddToCart = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please login to add to cart.",
      });
      return;
    }

    if (!productData?.prices[quantityDetails]) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please select a product variant.",
      });
      return;
    }

    setOptimisticCartState(true);

    const data = {
      priceId: productData.prices[quantityDetails]._id,
      quantity: productQuantity,
      productId: productData._id,
    };

    try {
      await dispatch(addItemToCart(data)).unwrap();
      context.setAlertBox({
        open: true,
        error: false,
        msg: "Product added to cart successfully!",
      });
    } catch (error) {
      setOptimisticCartState(false);
      console.error("Add to cart error:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg:
          typeof error === "string"
            ? error
            : error?.message || "Failed to add to cart.",
      });
    }
  }, [productData, quantityDetails, productQuantity, dispatch, context]);

  const handleToggleWishlist = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please login to manage wishlist.",
      });
      return;
    }

    if (!productData?.prices[quantityDetails]) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please select a product variant.",
      });
      return;
    }

    // Optimistically update UI
    const newState = !isInWishlist;
    setOptimisticWishlistState(newState);

    try {
      await dispatch(toggleWishlistItem(productData._id)).unwrap();
      const action = newState ? "added to" : "removed from";
      context.setAlertBox({
        open: true,
        error: false,
        msg: `Product ${action} wishlist.`,
      });
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticWishlistState(!newState);
      context.setAlertBox({
        open: true,
        error: true,
        msg: error || "Failed to update wishlist.",
      });
    }
  }, [productData, quantityDetails, isInWishlist, dispatch, context]);

  const gotoReviews = useCallback(() => {
    window.scrollTo({
      top: 550,
      behavior: "smooth",
    });
    setActiveTabs(2);
  }, []);

  const handleShare = useCallback(async () => {
    const shareData = {
      title: productData?.name || "Check out this product",
      text: `${productData?.name} - ${
        productData?.shortDescription ||
        "Amazing product available on SabbaFarm"
      }`,
      url: window.location.href,
    };

    try {
      // Try to use Web Share API (works on mobile and some desktop browsers)
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(window.location.href);
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Product link copied to clipboard!",
        });
      }
    } catch (error) {
      console.error("Share error:", error);
      // Final fallback - copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Product link copied to clipboard!",
        });
      } catch (clipboardError) {
        console.error("Clipboard error:", clipboardError);
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Failed to share product. Please copy the URL manually.",
        });
      }
    }
  }, [productData, context]);

  if (!id) {
    return (
      <section className="productDetails section">
        <div className="container">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: "300px" }}
          >
            <p>Invalid product ID. Redirecting to products page...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${
          productData?.name || "Product"
        } - Buy Online at SabbaFarm`}</title>
        <meta
          name="description"
          content={`Buy ${
            productData?.name || "product"
          } at the best price on SabbaFarm. ${
            productData?.shortDescription || ""
          }`}
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content={`${productData?.name || "Product"} - SabbaFarm`}
        />
        <meta
          property="og:description"
          content={`Get ${productData?.name || "product"} now on SabbaFarm. ${
            productData?.shortDescription || ""
          }`}
        />
        <meta property="og:image" content={productData?.imageUrl || ""} />
        <meta
          property="og:url"
          content={`https://sabbafarm.com/product/${id}`}
        />
        <meta property="og:type" content="product" />
      </Helmet>
      <section className="productDetails section">
        <div className="container">
          {cartLoading || !productData ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: "300px" }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="row">
              <div className="col-md-4 pl-5 part1">
                <ProductZoom
                  images={productData.images}
                  discount={productData.discount}
                />
              </div>
              <div className="col-md-7 pl-5 pr-5 part2">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h2 className="hd text-capitalize mb-0">
                    {productData.name}
                  </h2>
                  <Tooltip title="Share this product" placement="top">
                    <Button
                      type="button"
                      className="btn-blue btn-lg btn-big btn-circle"
                      onClick={handleShare}
                      style={{
                        transition: "all 0.3s ease",
                        minWidth: "auto",
                        padding: "12px",
                      }}
                    >
                      <IoMdShare
                        style={{
                          fontSize: "20px",
                          transition: "all 0.3s ease",
                        }}
                      />
                    </Button>
                  </Tooltip>
                </div>
                <ul className="list list-inline d-flex align-items-center">
                  <li className="list-inline-item">
                    <div className="d-flex align-items-center">
                      <span className="text-light mr-2">Brands: </span>
                      <span>{productData.brand}</span>
                    </div>
                  </li>
                  <li className="list-inline-item">
                    <div className="d-flex align-items-center">
                      <Rating
                        name="read-only"
                        value={parseInt(productData.rating) || 0}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                      <span
                        className="text-light cursor ml-2"
                        onClick={gotoReviews}
                      >
                        {reviewsData.length} Review
                        {reviewsData.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </li>
                </ul>
                <div className="d-flex info mb-3">
                  <span className="oldPrice">
                    Rs: {productData.prices[quantityDetails]?.oldPrice}
                  </span>
                  <span className="netPrice text-danger ml-2">
                    Rs: {productData.prices[quantityDetails]?.actualPrice}
                  </span>
                </div>
                {productData.prices[quantityDetails]?.countInStock >= 1 ? (
                  <span className="badge badge-success">IN STOCK</span>
                ) : (
                  <span className="badge badge-danger">OUT OF STOCK</span>
                )}
                <div className="container py-4">
                  <h2 className="mb-4">Variants</h2>
                  <div className="row g-3">
                    {productData.prices?.map((data, index) => (
                      <div
                        className={`col-6 col-md-4 col-lg-4 mt-3 border rounded text-center ${
                          quantityDetails === index
                            ? "border-warning border-lg"
                            : "bg-light"
                        }`}
                        style={{
                          border:
                            quantityDetails === index ? "4px solid yellow" : "",
                        }}
                        onClick={() => setQuantityDetails(index)}
                        key={index}
                      >
                        <div
                          className="bg-warning mx-auto font-bold text-white"
                          style={{
                            borderBottomRightRadius: "10px",
                            borderBottomLeftRadius: "10px",
                            backgroundColor:
                              quantityDetails === index ? "#4dffa6" : "",
                            fontWeight: "700",
                            fontSize: "10px",
                            maxWidth: "70px",
                          }}
                        >
                          {data.discount}% OFF
                        </div>
                        <h5 className="fw-bold font-semibold my-2">
                          {data.quantity} {data.type}
                        </h5>
                        <span className="text-success fw-bold font-extrabold my-2">
                          ₹{data.actualPrice}
                        </span>
                        <span
                          className="text-decoration-line-through text-muted ml-2 my-2"
                          style={{ textDecoration: "line-through" }}
                        >
                          ₹{data.oldPrice}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="d-flex align-items-center mt-3 actions_">
                  <QuantityBox
                    quantity={quantity}
                    item={productData}
                    selectedItem={() => {}}
                    index={quantityDetails}
                    isInCart={isInCart}
                  />
                  <div className="d-flex align-items-center btnActions">
                    {productData.prices[quantityDetails]?.countInStock >= 1 ? (
                      <Button
                        className={`btn-blue btn-lg btn-big btn-round ${
                          isInCart
                            ? "bg-gray opacity-50 cursor-not-allowed"
                            : "bg-red"
                        }`}
                        onClick={handleAddToCart}
                        disabled={isInCart}
                      >
                        {isInCart ? "Added to Cart" : "Add to Cart"}
                      </Button>
                    ) : (
                      <Button
                        className="btn-blue btn-lg btn-big btn-round bg-red opacity-50 cursor-not-allowed"
                        disabled
                      >
                        Out of Stock
                      </Button>
                    )}
                    <Tooltip
                      title={
                        isInWishlist
                          ? "Remove from Wishlist"
                          : "Add to Wishlist"
                      }
                      placement="top"
                    >
                      <Button
                        type="button"
                        className="btn-blue btn-lg btn-big btn-circle ml-4"
                        onClick={handleToggleWishlist}
                        style={{
                          transition: "all 0.3s ease",
                          transform: isInWishlist ? "scale(1.05)" : "scale(1)",
                        }}
                      >
                        {isInWishlist ? (
                          <FaHeart
                            className="text-danger"
                            style={{
                              fontSize: "20px",
                              color: "#ff4757",
                              transition: "all 0.3s ease",
                            }}
                          />
                        ) : (
                          <FaRegHeart
                            style={{
                              fontSize: "20px",
                              transition: "all 0.3s ease",
                            }}
                          />
                        )}
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          )}
          <br />
          <div className="card mt-5 p-5 detailsPageTabs">
            <div className="customTabs">
              <ul className="list list-inline">
                <li className="list-inline-item">
                  <Button
                    className={`tab-button ${activeTabs === 0 ? "active" : ""}`}
                    onClick={() => setActiveTabs(0)}
                  >
                    Description
                  </Button>
                </li>
                <li className="list-inline-item">
                  <Button
                    className={`tab-button ${activeTabs === 2 ? "active" : ""}`}
                    onClick={() => setActiveTabs(2)}
                  >
                    Reviews ({reviewsData.length})
                  </Button>
                </li>
              </ul>
              <br />
              {activeTabs === 0 && (
                <div
                  className="tabContent p-4 bg-light rounded shadow-sm"
                  style={{
                    fontSize: "1rem",
                    lineHeight: "1.8",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                    borderLeft: "4px solid #16a34a",
                    fontFamily: "Segoe UI, sans-serif",
                  }}
                >
                  <h4
                    className="mb-3"
                    style={{ color: "#16a34a", fontWeight: 600 }}
                  >
                    Product Description
                  </h4>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        productData?.description || "No description available."
                      ),
                    }}
                  />
                </div>
              )}
              {activeTabs === 2 && (
                <div className="tabContent">
                  <div className="row">
                    <div className="col-md-8">
                      <h3>Customer Questions & Answers</h3>
                      {reviewsData?.length > 0 &&
                        reviewsData.slice(0).map((item, index) => (
                          <div
                            className="reviewBox mb-3 pb-2 border-bottom"
                            key={index}
                            style={{
                              padding: "12px",
                              borderRadius: "6px",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <div className="info">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <div
                                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-3"
                                    style={{
                                      width: "36px",
                                      height: "36px",
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                    }}
                                  >
                                    {item.customerName?.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <h5
                                      className="mb-1"
                                      style={{
                                        fontSize: "1.1rem",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {item.customerName}
                                    </h5>
                                    <span
                                      className="text-muted"
                                      style={{ fontSize: "0.85rem" }}
                                    >
                                      {item.dateCreated?.split("T")[0]}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <Rating
                                    name="half-rating-read"
                                    value={item.customerRating}
                                    readOnly
                                    size="medium"
                                    precision={0.5}
                                  />
                                </div>
                              </div>
                              <p
                                className="mt-2 mb-0"
                                style={{
                                  fontSize: "0.95rem",
                                  color: "#333",
                                  lineHeight: "1.4",
                                }}
                              >
                                {item.review}
                              </p>
                            </div>
                          </div>
                        ))}
                      <form className="reviewForm" onSubmit={addReview}>
                        <h4>Add a Review</h4>
                        <div className="form-group">
                          <textarea
                            className="form-control shadow"
                            placeholder="Write a Review"
                            name="reviewText"
                            value={reviews.reviewText}
                            onChange={onChangeInput}
                          />
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <Rating
                                name="rating"
                                value={rating}
                                precision={1}
                                onChange={changeRating}
                              />
                            </div>
                          </div>
                        </div>
                        <br />
                        <div className="form-group">
                          <Button
                            type="submit"
                            className="btn-blue btn-lg btn-big btn-round"
                            disabled={cartLoading}
                          >
                            {cartLoading ? (
                              <CircularProgress
                                color="inherit"
                                className="loader"
                              />
                            ) : (
                              "Submit Review"
                            )}
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <br />
          {relatedProductData.length > 0 && (
            <RelatedProducts
              title="RELATED PRODUCTS"
              data={relatedProductData}
            />
          )}
        </div>
      </section>
    </>
  );
};

export default ProductDetails;
